import {
  createFirmVirtualBusinessCardV1,
  deleteFirmVirtualBusinessCardV1,
  retrieveFirmVirtualBusinessCardsV1,
} from "@/lib/data/requests/firm/firm-business-card";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIResponse, FirmVirtualBusinessCard } from "@/lib/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

export const getFirmVirtualBusinessCardsQueryOptions = () => {
  return {
    queryKey: [queryKeys.firmVirtualBusinessCards],
    queryFn: async () => retrieveFirmVirtualBusinessCardsV1(),
  };
};

export const useCreateFirmVirtualBusinessCard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createFirmVirtualBusinessCardV1>[0]) =>
      createFirmVirtualBusinessCardV1(data),
    onError: () => {
      toast.error("Error creating firm virtual business card");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmVirtualBusinessCards],
      });
    },
    onSuccess: () => {
      toast.success("Firm virtual business card created successfully");
    },
  });
};

export const useDeleteFirmVirtualBusinessCard = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteFirmVirtualBusinessCardV1>[0]) =>
      deleteFirmVirtualBusinessCardV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.firmVirtualBusinessCards],
        (oldData: APIResponse<FirmVirtualBusinessCard[]> | undefined) => {
          if (!oldData) return;
          return {
            data: oldData.data.filter((card) => card.id !== data.id),
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.firmVirtualBusinessCards,
        ]),
      };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.firmVirtualBusinessCards],
          context.previousData,
        );
      }
      toast.error("Error deleting firm virtual business card");
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmVirtualBusinessCards],
      });
    },
    onSuccess: () => {
      toast.success("Firm virtual business card deleted successfully");
    },
  });
};
